/*
  Showdowns — the Club voting SPA engine. A faithful port of the design mockup
  (listing + detail + the vote moment), wired to the live rewards API.

  Each `.kkumi-showdowns` mount is an instance:
    - data-showdown=""                       → hub: card listing + in-page detail
    - data-detail-only="1" data-showdown=<s> → one showdown, opened to its detail

  Reads GET /apps/rewards/showdowns and POSTs /apps/rewards/vote. Three methods:
  single (pick one) / approve (tick any) / ranked (tap to order, Borda-weighted).

  Reuse, per the repo rules: every API-controlled string is escaped with
  window.KKEsc before innerHTML (the inline icon set + merchant copy are trusted
  constants); the submit button uses window.KKBtn; window.KKRewards is invalidated
  after a points-earning vote. All customer copy comes from the section's
  data-copy JSON ({token} placeholders filled here) so it stays merchant-editable.
*/
(function () {
  'use strict';
  var esc = window.KKEsc || function (s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); };
  var DAY = 86400000, HOUR = 3600000, MIN = 60000;
  function root() { return ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, ''); }

  /* design glyphs — trusted constant SVGs (not API data) */
  var I = {
    clock: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7.5V12l3 1.8"/></svg>',
    check: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6.5 9 17.5l-5-5"/></svg>',
    star: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"/></svg>',
    heart: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.001 5.529c2.349-2.532 6.151-2.532 8.5 0 2.35 2.531 2.35 6.638 0 9.17l-7.793 8.025a1 1 0 0 1-1.414 0L3.501 14.7c-2.35-2.531-2.35-6.638 0-9.17 2.349-2.532 6.152-2.532 8.501 0Z"/></svg>',
    list: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6h12M9 12h9M9 18h5"/><circle cx="4.5" cy="6" r="1.2" fill="currentColor" stroke="none"/><circle cx="4.5" cy="12" r="1.2" fill="currentColor" stroke="none"/><circle cx="4.5" cy="18" r="1.2" fill="currentColor" stroke="none"/></svg>',
    trophy: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 4h10v4a5 5 0 0 1-10 0z"/><path d="M7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3"/><path d="M12 13v4M9 21h6M10 17h4"/></svg>',
    arrow: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h13.5M13 6.5l6 5.5-6 5.5"/></svg>',
    back: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M19 12H6M11 6l-6 6 6 6"/></svg>',
    info: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><circle cx="12" cy="8" r="0.6" fill="currentColor" stroke="none"/></svg>',
    spark: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.5 13.4 8a3 3 0 0 0 2.6 2.6L21.5 12 16 13.4a3 3 0 0 0-2.6 2.6L12 21.5 10.6 16A3 3 0 0 0 8 13.4L2.5 12 8 10.6A3 3 0 0 0 10.6 8L12 2.5Z"/></svg>',
    bolt: '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 2.5a.5.5 0 0 1 .47.685L11.85 9h5.84a.5.5 0 0 1 .39.81l-9.5 11.5a.5.5 0 0 1-.87-.435L9.85 14H4.31a.5.5 0 0 1-.39-.81l9.05-10.5a.5.5 0 0 1 .53-.19Z"/></svg>'
  };

  function initInstance(rootEl) {
    if (rootEl.dataset.kkBound) return;
    rootEl.dataset.kkBound = '1';

    var T = {
      layout: rootEl.dataset.layout || 'grid',
      countdown: rootEl.dataset.countdown || 'hero',
      standings: rootEl.dataset.standings || 'after',
      framing: rootEl.dataset.framing || 'split',
      urgency: rootEl.dataset.urgency !== 'false'
    };
    var detailOnly = rootEl.dataset.detailOnly === '1';
    var slugFilter = rootEl.dataset.showdown || '';
    var basePath = window.location.pathname;
    var C = {};
    try { C = JSON.parse(rootEl.dataset.copy || '{}'); } catch (e) { C = {}; }

    // Built-in English copy. The section's data-copy overrides any of these; the
    // candidate section (and any un-set key) falls back here, so copy always
    // renders even where it isn't exposed as a merchant setting.
    var DEFAULTS = {
      method_fav_tag: 'Pick one', method_fav_verb: 'Pick your favourite', method_fav_rule: 'one pick each',
      method_approve_tag: 'Approve any', method_approve_verb: 'Approve any you’d love', method_approve_rule: 'tick as many as you like',
      method_ranked_tag: 'Rank them', method_ranked_verb: 'Rank your order', method_ranked_rule: '1st, 2nd, 3rd…',
      status_open: 'Voting open', status_hit: 'On our radar', status_closed: 'Closed', voted_short: 'Voted', candidates_n: '{n} candidates',
      days_left: '{days} left', closing_today: 'Closing today', closed_on: 'Closed {date}', unit_day: 'day',
      cta_vote: 'Vote now', cta_standings: 'View standings', cta_results: 'See results',
      meter_unit: 'votes', meter_togo: '{n} to go', meter_reached: 'Goal reached ✓', meter_commit: 'At {goal} votes we commit to producing the winner', meter_short: 'Fell short of the goal',
      cd_closes: 'Voting closes in', cd_closed: 'Voting closed', cd_final: 'final hours', cd_left: 'left', cd_ended: 'ended',
      unit_days: 'days', unit_hrs: 'hrs', unit_min: 'min', unit_sec: 'sec',
      fact_how: 'How it works', fact_goal: 'Vote-point goal', fact_goal_unit: 'pts', fact_goal_sub: 'a signal, not a promise',
      banner_hit_t: 'It hit {goal}, we’ll consider it', banner_hit_d: 'Crossing the vote-point goal doesn’t guarantee production, but it tells us there’s real demand. This one’s now on our radar.',
      banner_voted_t: 'Your vote is in', banner_voted_d: 'Thanks for weighing in. You can change your vote any time before voting closes.',
      banner_closed_t: 'Showdown closed', banner_closed_d: 'Winner: <b>{winner}</b> with {pct}% of the weight. It’s locked in for the next run.',
      banner_failed_t: 'Showdown closed, goal not reached', banner_failed_d: 'This one didn’t reach its {goal}-vote goal, so nothing was greenlit this time. Thanks to everyone who voted; there’ll be more.',
      poll_cast: 'Cast your vote', poll_candidates: 'Candidates',
      count_choose: 'choose 1', count_picked: '1 picked', count_approved: '{n} approved', count_ranked: '{n} ranked',
      submit_fav: 'Cast my vote', submit_approve: 'Submit approvals', submit_ranked: 'Submit ranking',
      submit_fav_upd: 'Update my pick', submit_approve_upd: 'Update approvals', submit_ranked_upd: 'Update my ranking',
      imgslot_art: 'art',
      keep_vote: 'Keep my current vote', change_vote: 'Change my vote', login_cta: 'Log in to vote', submitting: 'Saving…',
      cooldown_note: 'You can only change your vote once every 5 minutes',
      stand_live: 'Live standings', stand_final: 'Final results', stand_meta: '{n} members · {unit}', stand_hidden: 'hidden until close',
      unit_votes: 'votes', unit_ranked: 'ranked weight', unit_pts: 'pts', your_vote: 'Your vote', your_rank: 'Your #{n}',
      lead_leads: '{name} leads with {pct}% of the vote', lead_won: '{name} won with {pct}% of the weight', lead_failed: 'Fell short of the {goal}-vote goal, nothing greenlit',
      toast_counted_t: 'Vote counted, +1 point', toast_counted_d: '{name} is leading with {pct}%.',
      toast_updated_t: 'Vote updated', toast_updated_d: 'Standings refreshed, {name} now at {pct}%.',
      toast_goal_t: 'Goal reached, {goal} points!', toast_goal_d: 'We’ll seriously consider making this. You pushed it over.',
      toast_err_t: 'Couldn’t save your vote', toast_err_d: 'Please try again in a moment.', load_err: 'Couldn’t load showdowns right now.'
    };
    function t(key, vars) {
      var s = C[key] != null && C[key] !== '' ? String(C[key]) : (DEFAULTS[key] != null ? DEFAULTS[key] : '');
      if (vars) for (var k in vars) if (vars.hasOwnProperty(k)) s = s.split('{' + k + '}').join(vars[k]);
      return s;
    }
    function plural(n, w) { return n + ' ' + w + (n === 1 ? '' : 's'); }

    /* method vocab — copy is merchant-editable */
    var METHOD = {
      favourite: { tag: t('method_fav_tag') || 'Pick one', glyph: I.heart, verb: t('method_fav_verb') || 'Pick your favourite', rule: t('method_fav_rule') || 'one pick each' },
      approve: { tag: t('method_approve_tag') || 'Approve any', glyph: I.check, verb: t('method_approve_verb') || 'Approve any you’d love', rule: t('method_approve_rule') || 'tick as many as you like' },
      ranked: { tag: t('method_ranked_tag') || 'Rank them', glyph: I.list, verb: t('method_ranked_verb') || 'Rank your order', rule: t('method_ranked_rule') || '1st, 2nd, 3rd…' }
    };

    /* ---- data + normalisation (API → display shape) ---- */
    var DATA = [], loggedIn = false, current = null, reloadQueued = false;
    var METHOD_MAP = { single: 'favourite', approve: 'approve', ranked: 'ranked' };

    // "Change my vote" cooldown — after casting a vote the change button is locked
    // for 5 minutes. This both rate-limits re-votes AND covers the in-flight POST
    // (the button can't be clicked while the optimistic vote is still saving). The
    // timestamp is persisted per-slug so a reload can't bypass it.
    var VOTE_COOLDOWN_MS = 5 * 60 * 1000, cooldownTimer = null;
    function cdKey(slug) { return 'kksd_lv_' + slug; }
    function markVoted(slug) { try { localStorage.setItem(cdKey(slug), String(Date.now())); } catch (e) { /* private mode */ } }
    function cooldownLeft(slug) {
      var at; try { at = Number(localStorage.getItem(cdKey(slug))) || 0; } catch (e) { at = 0; }
      return Math.max(0, VOTE_COOLDOWN_MS - (Date.now() - at));
    }

    function normalise(api) {
      return api.map(function (s) {
        var method = METHOD_MAP[s.method] || 'favourite';
        var voters = s.voter_count || 0;
        var goal = s.point_goal || 0;
        var status;
        if (s.phase === 'decided' || s.phase === 'closed') status = 'closed';
        else if (goal && voters >= goal) status = 'hit';
        else status = 'open';
        var my = s.my_votes || [];
        var mySel = {};
        if (method === 'favourite') mySel.pick = my[0] || null;
        else if (method === 'approve') mySel.approve = my.slice();
        else mySel.rank = my.slice();
        return {
          id: s.slug, slug: s.slug, method: method, status: status, voted: !!s.voted,
          title: s.title || '', blurb: s.subtitle || s.description || '',
          description: s.description || '',
          threshold: goal, points: voters, winnerId: s.winner ? s.winner.id : null,
          opens: s.opens_at ? new Date(s.opens_at).getTime() : null,
          closes: s.closes_at ? new Date(s.closes_at).getTime() : null,
          hero: s.hero_image_url || null, gallery: s.gallery || [],
          candidates: (s.candidates || []).map(function (c) {
            return { id: c.id, name: c.name || '', sub: c.subtitle || '', desc: c.blurb || '', img: c.image_url || null,
              price: c.intended_price_cents != null ? c.intended_price_cents : null,
              votes: c.votes || 0, weight: c.weight || 0 };
          }),
          mySel: mySel
        };
      });
    }
    function find(id) { for (var i = 0; i < DATA.length; i++) if (DATA[i].id === id) return DATA[i]; return null; }

    /* ---- helpers ---- */
    function fmtDate(ts) { return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }); }
    function daysLeft(c) { return Math.max(0, Math.ceil((c - Date.now()) / DAY)); }
    function hoursLeft(c) { return Math.max(0, (c - Date.now()) / HOUR); }
    function money(c) { return c == null ? '' : window.KKMoney.fromCents(c, { trim: true, shop: true }); }
    function weightOf(c) { return c.weight || 0; }
    function selCount(s) { var m = s.mySel; if (s.method === 'favourite') return m.pick ? 1 : 0; if (s.method === 'approve') return (m.approve || []).length; return (m.rank || []).length; }
    function isMine(s, cid) { var m = s.mySel; if (s.method === 'favourite') return m.pick === cid; if (s.method === 'approve') return (m.approve || []).indexOf(cid) > -1; return (m.rank || []).indexOf(cid) > -1; }
    function myRankOf(s, cid) { return (s.mySel.rank || []).indexOf(cid); }
    // Weights only change on reload (the client never mutates them), and
    // normalise() makes fresh objects each load, so memoising on `s` is safe and
    // saves the 5-7 redundant sorts a single detail render would otherwise do.
    function standings(s) {
      if (s._st) return s._st;
      var arr = s.candidates.map(function (c) { return { c: c, w: weightOf(c) }; });
      var total = arr.reduce(function (a, b) { return a + b.w; }, 0) || 1;
      arr.forEach(function (o) { o.pct = Math.round((o.w / total) * 100); });
      arr.sort(function (a, b) { return b.w - a.w; });
      s._st = arr;
      return arr;
    }
    function leader(s) { return standings(s)[0] || { c: { name: '' }, pct: 0 }; }
    /* who to crown on a closed/decided showdown — the admin-picked winner if set, else the vote leader. */
    function topResult(s) {
      if (s.status === 'closed' && s.winnerId != null) {
        var st = standings(s);
        for (var i = 0; i < st.length; i++) if (st[i].c.id === s.winnerId) return st[i];
      }
      return leader(s);
    }
    // The vote-point goal is the public bar. A showdown with a goal that closes
    // below it FAILED — nothing is crowned (so a sub-threshold close never reads as
    // "X won"). No goal set → it's a plain poll, so the leader still wins.
    function reachedGoal(s) { return s.threshold > 0 ? s.points >= s.threshold : true; }
    function failed(s) { return s.status === 'closed' && !reachedGoal(s); }
    function shareOf(s, c) { var st = standings(s); for (var i = 0; i < st.length; i++) if (st[i].c.id === c.id) return st[i].pct; return 0; }
    function imgslot(label) { return '<div class="kk-imgslot">' + esc(label) + '</div>'; }
    function pic(url, label) { return url ? '<img src="' + esc(url) + '" alt="" loading="lazy">' : imgslot(label); }

    /* ---- meter ---- */
    function meter(s, opts) {
      opts = opts || {};
      if (!s.threshold) return '';
      var p = Math.min(100, Math.round((s.points / s.threshold) * 100));
      var done = s.points >= s.threshold;
      var sub = done ? t('meter_reached')
        : (s.status === 'closed' ? t('meter_short')
          : (opts.lg ? t('meter_commit', { goal: s.threshold }) : t('meter_togo', { n: Math.max(0, s.threshold - s.points) })));
      return '<div class="meter' + (opts.lg ? ' meter--lg' : '') + '">' +
        '<div class="meter__top"><span class="meter__big"><b>' + s.points + '</b> / ' + s.threshold + ' ' + esc(t('meter_unit') || 'votes') + '</span><span class="meter__sub">' + sub + '</span></div>' +
        '<div class="meter__track"><span class="meter__fill' + (done ? ' meter__fill--lit' : '') + '" style="width:' + p + '%"></span>' + (p < 100 ? '<div class="meter__tick" style="left:100%"></div>' : '') + '</div></div>';
    }

    /* ---- listing card ---- */
    function statusBadge(s) {
      if (s.status === 'closed') return '<span class="sbadge sbadge--closed">' + esc(t('status_closed') || 'Closed') + '</span>';
      if (s.status === 'hit') return '<span class="sbadge sbadge--hit">' + I.star + ' ' + esc(t('status_hit') || 'On our radar') + '</span>';
      return '<span class="sbadge sbadge--open"><span class="pulse"></span> ' + esc(t('status_open') || 'Voting open') + '</span>';
    }
    function card(s) {
      var open = s.status !== 'closed';
      var dl = daysLeft(s.closes || 0);
      var closedTxt = !open ? t('closed_on', { date: s.closes ? fmtDate(s.closes) : '' }) : (!s.closes ? '' : (hoursLeft(s.closes) < 24 ? t('closing_today') : t('days_left', { n: dl, days: plural(dl, t('unit_day') || 'day') })));
      var urgent = open && s.closes && hoursLeft(s.closes) <= 48 && T.urgency;
      var voted = s.voted ? '<span class="vcard__dot"></span><span class="daysleft">' + I.check + ' ' + esc(t('voted_short') || 'Voted') + '</span>' : '';
      var hero = s.hero || (s.candidates[0] && s.candidates[0].img) || null;
      var a = document.createElement('button');
      a.className = 'vcard'; a.type = 'button'; a.setAttribute('data-open', s.id);
      a.innerHTML =
        '<div class="vcard__media">' + pic(hero, s.title) +
          '<div class="vcard__badges"><span class="mtag">' + METHOD[s.method].glyph + ' ' + esc(METHOD[s.method].tag) + '</span><div class="right">' + statusBadge(s) + '</div></div></div>' +
        '<div class="vcard__body">' +
          '<h3 class="vcard__title">' + esc(s.title) + '</h3>' +
          '<div class="vcard__meta"><span>' + esc(t('candidates_n', { n: s.candidates.length })) + '</span>' + (closedTxt ? '<span class="vcard__dot"></span><span class="daysleft' + (urgent ? ' urgent' : '') + '">' + I.clock + ' ' + esc(closedTxt) + '</span>' : '') + voted + '</div>' +
          meter(s) +
          '<div class="vcard__foot"><span class="vcard__cta">' + esc(s.status === 'closed' ? (t('cta_results') || 'See results') : (s.voted ? (t('cta_standings') || 'View standings') : (t('cta_vote') || 'Vote now'))) + ' <span class="arr">' + I.arrow + '</span></span></div>' +
        '</div>';
      a.addEventListener('click', function () { openDetail(s.id, true); });
      return a;
    }

    /* ---- candidate tile (voting) ---- */
    function tile(s, c, interactive) {
      var b = document.createElement(interactive ? 'button' : 'div');
      if (interactive) b.type = 'button';
      b.className = 'ctile'; b.setAttribute('data-cid', c.id);
      var sel = isMine(s, c.id), ctrl;
      if (s.method === 'favourite') { if (sel) b.classList.add('is-sel'); ctrl = '<span class="ctile__sel">' + I.check + '</span>'; }
      else if (s.method === 'approve') { if (sel) b.classList.add('is-sel'); ctrl = '<span class="ctile__sel sq">' + I.check + '</span>'; }
      else { var r = myRankOf(s, c.id); b.setAttribute('data-rank', r > -1 ? (r + 1) : 0); ctrl = '<span class="ctile__rank">' + (r > -1 ? (r + 1) : '+') + '</span>'; }
      b.innerHTML =
        '<span class="ctile__pic">' + pic(c.img, t('imgslot_art')) + '</span>' +
        '<span class="ctile__main">' +
          '<span class="ctile__name">' + esc(c.name) + '</span>' +
          (c.sub ? '<span class="ctile__sub">' + esc(c.sub) + '</span>' : '') +
          (c.desc ? '<span class="ctile__desc">' + esc(c.desc) + '</span>' : '') +
          (c.price != null ? '<span class="ctile__price">' + esc(money(c.price)) + '</span>' : '') +
          '<span class="ctile__share"><span class="ctile__share-bar"><span style="width:' + shareOf(s, c) + '%"></span></span><span class="ctile__share-pct">' + shareOf(s, c) + '%</span></span>' +
        '</span>' + ctrl;
      if (interactive) b.addEventListener('click', function () { pickCandidate(s, c.id); });
      return b;
    }

    /* ---- standings panel ---- */
    function standingsPanel(s, opts) {
      opts = opts || {};
      var blind = opts.blind, st = standings(s), open = s.status !== 'closed';
      var wrap = document.createElement('div'); wrap.className = 'standings';
      wrap.innerHTML = '<div class="standings__head"><h3>' + esc(open ? (t('stand_live') || 'Live standings') : (t('stand_final') || 'Final results')) + '</h3>' +
        '<span class="standings__meta">' + esc(t('stand_meta', { n: s.points, unit: s.method === 'ranked' ? (t('unit_ranked') || 'ranked weight') : (t('unit_votes') || 'votes') })) + (blind ? ' · ' + esc(t('stand_hidden') || 'hidden until close') : '') + '</span></div>';
      var crownable = !failed(s); // a failed (sub-threshold) close crowns nobody
      st.forEach(function (o, i) {
        var win = crownable && ((s.status === 'closed' && s.winnerId != null) ? (o.c.id === s.winnerId) : (i === 0 && (s.status === 'closed' || s.points > 0)));
        var mine = isMine(s, o.c.id);
        var mineTag = mine ? '<span class="srow__mine">' + I.check + ' ' + esc(s.method === 'ranked' ? t('your_rank', { n: myRankOf(s, o.c.id) + 1 }) : (t('your_vote') || 'Your vote')) + '</span>' : '';
        var row = document.createElement('div');
        row.className = 'srow' + (win ? ' win' : '') + (blind ? ' blind' : '');
        row.setAttribute('data-cid', o.c.id);
        row.innerHTML =
          '<span class="srow__rank">' + (win ? I.trophy : (i + 1)) + '</span>' +
          '<span class="srow__main"><span class="srow__name">' + esc(o.c.name) + mineTag + '</span><span class="srow__bar"><span class="srow__fill" data-fill style="width:0%"></span></span></span>' +
          '<span class="srow__pct"><span data-pct>' + (blind ? '••' : o.pct + '%') + '</span>' + (blind ? '' : '<small class="srow__count">' + weightOf(o.c) + ' ' + esc(s.method === 'ranked' ? (t('unit_pts') || 'pts') : (t('unit_votes') || 'votes')) + '</small>') + '</span>';
        wrap.appendChild(row);
      });
      return wrap;
    }
    function animateStandings(scope, s, fromZero) {
      var prev = s._pct || {}, st = standings(s), map = {};
      st.forEach(function (o) { map[o.c.id] = o.pct; });
      scope.querySelectorAll('.srow').forEach(function (row) {
        var cid = Number(row.getAttribute('data-cid')), target = map[cid] || 0;
        var start = fromZero ? 0 : (prev[cid] != null ? prev[cid] : 0);
        var fill = row.querySelector('[data-fill]'), pctEl = row.querySelector('[data-pct]');
        if (fill) { fill.style.width = start + '%'; requestAnimationFrame(function () { fill.style.width = target + '%'; }); }
        if (pctEl && pctEl.textContent.indexOf('•') < 0) countNum(pctEl, start, target, 750);
      });
      s._pct = map;
    }
    function countNum(el, from, to, dur) {
      var t0 = null;
      function ease(k) { return 1 - Math.pow(1 - k, 3); }
      function step(ts) { if (t0 == null) t0 = ts; var k = Math.min(1, (ts - t0) / dur); el.textContent = Math.round(from + (to - from) * ease(k)) + '%'; if (k < 1) requestAnimationFrame(step); }
      requestAnimationFrame(step);
    }

    /* ---- countdown (blocks / hero / ring + urgency) ---- */
    function parts(ms) { if (ms <= 0) return null; return { d: Math.floor(ms / DAY), h: Math.floor((ms % DAY) / HOUR), m: Math.floor((ms % HOUR) / MIN), s: Math.floor((ms % MIN) / 1000) }; }
    function cdInner(s) {
      var open = s.status !== 'closed', ms = (s.closes || 0) - Date.now(), p = parts(ms);
      if (!open || !p || !s.closes) return '<div class="cd__unit"><span class="cd__num">—</span><span class="cd__lab">' + esc(t('cd_ended') || 'ended') + '</span></div>';
      var pad = function (n) { return String(n).padStart(2, '0'); };
      var L = { d: t('unit_days') || 'days', h: t('unit_hrs') || 'hrs', m: t('unit_min') || 'min', s: t('unit_sec') || 'sec', day: t('unit_day') || 'day' };
      // Lead with the largest unit still ticking: days while ≥1 day remains,
      // then hours under a day, minutes under an hour, seconds under a minute.
      // Larger (now-zero) units drop; smaller ones trail. So the headline is
      // never a useless "0 days".
      var order = ['d', 'h', 'm', 's'], li = 0;
      while (li < 3 && p[order[li]] === 0) li++;
      var lead = order[li], rest = order.slice(li + 1);
      var labelOf = function (k) { return k === 'd' && p.d === 1 ? L.day : L[k]; };
      var restUnit = function (k) { return '<div class="cd-hero__u"><span class="cd-hero__n">' + pad(p[k]) + '</span><span class="cd-hero__l">' + esc(L[k]) + '</span></div>'; };
      if (T.countdown === 'hero') {
        return '<div class="cd-hero"><div><div class="cd-hero__big">' + p[lead] + '</div><div class="cd-hero__bigl">' + esc(labelOf(lead) + ' ' + (t('cd_left') || 'left')) + '</div></div>' +
          (rest.length ? '<div class="cd-hero__rest">' + rest.map(restUnit).join('') + '</div>' : '') + '</div>';
      }
      if (T.countdown === 'ring') {
        var span = (s.opens && s.closes > s.opens) ? (s.closes - s.opens) : (14 * DAY);
        var frac = Math.max(0, Math.min(1, ms / span)), r = 39, circ = 2 * Math.PI * r, off = circ * (1 - frac);
        return '<div class="cd-ring"><div class="cd-ring__wrap">' +
          '<svg class="cd-ring__svg" viewBox="0 0 92 92"><defs><linearGradient id="kksd-cdgrad" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="var(--kk-primary-fill,#f279a1)"/><stop offset="1" stop-color="var(--kk-pink-hot,#E0277F)"/></linearGradient></defs>' +
          '<circle class="cd-ring__track" cx="46" cy="46" r="' + r + '"/><circle class="cd-ring__bar" cx="46" cy="46" r="' + r + '" stroke-dasharray="' + circ.toFixed(1) + '" stroke-dashoffset="' + off.toFixed(1) + '"/></svg>' +
          '<div class="cd-ring__center"><div><div class="cd-ring__n">' + p[lead] + '</div><div class="cd-ring__l">' + esc(labelOf(lead)) + '</div></div></div></div>' +
          (rest.length ? '<div class="cd-ring__rest">' + rest.map(restUnit).join('') + '</div>' : '') + '</div>';
      }
      function u(n, l) { return '<div class="cd__unit"><span class="cd__num">' + pad(n) + '</span><span class="cd__lab">' + esc(l) + '</span></div>'; }
      return [lead].concat(rest).map(function (k) { return u(p[k], labelOf(k)); }).join('');
    }
    function countdownHTML(s) {
      var open = s.status !== 'closed';
      var urgent = open && T.urgency && s.closes && hoursLeft(s.closes) <= 48;
      var styleCls = T.countdown === 'hero' ? ' countdown--hero' : (T.countdown === 'ring' ? ' countdown--ring' : '');
      return '<div class="countdown' + styleCls + (open ? '' : ' ended') + (urgent ? ' urgent' : '') + '" data-cd-host data-id="' + esc(s.id) + '">' +
        '<div class="countdown__row"><span class="countdown__label"><span class="countdown__lab-main">' + I.clock + ' ' + esc(open ? (t('cd_closes') || 'Voting closes in') : (t('cd_closed') || 'Voting closed')) + '</span>' + (urgent ? '<span class="urgent-flag">' + I.bolt + ' ' + esc(t('cd_final') || 'final hours') + '</span>' : '') + '</span><div class="cd" data-cd>' + cdInner(s) + '</div></div></div>';
    }
    function tickCountdowns() {
      rootEl.querySelectorAll('[data-cd-host]').forEach(function (host) {
        var s = find(host.getAttribute('data-id')); if (!s) return;
        var box = host.querySelector('[data-cd]'); if (box) box.innerHTML = cdInner(s);
        if (s.closes && s.status !== 'closed' && (s.closes - Date.now()) <= 0 && !reloadQueued) { reloadQueued = true; setTimeout(load, 1200); }
      });
    }

    /* ---- small builders ---- */
    function fact(ic, k, v, sub) { return '<div class="fact"><div class="fact__k">' + ic + ' ' + esc(k) + '</div><div class="fact__v">' + esc(v) + (sub ? '<small>' + esc(sub) + '</small>' : '') + '</div></div>'; }
    function bannerHTML(kind, ic, head, desc) { return '<div class="banner banner--' + kind + '"><div class="banner__ic">' + ic + '</div><div><p class="banner__t">' + head + '</p><p class="banner__d">' + desc + '</p></div></div>'; }
    function leadLine(s, big) {
      // A failed (sub-threshold) close crowns nobody — show "fell short", not a winner.
      if (failed(s)) {
        return '<div class="leadline leadline--muted' + (big ? ' leadline--lg' : '') + '"><span class="leadline__ic">' + I.info + '</span><span class="leadline__t">' + esc(t('lead_failed', { goal: s.threshold })) + '</span></div>';
      }
      var Lr = topResult(s);
      var txt = s.status === 'closed' ? t('lead_won', { name: esc(Lr.c.name), pct: Lr.pct }) : t('lead_leads', { name: esc(Lr.c.name), pct: Lr.pct });
      return '<div class="leadline' + (big ? ' leadline--lg' : '') + '"><span class="leadline__ic">' + I.trophy + '</span><span class="leadline__t">' + txt + '</span></div>';
    }

    /* ---- DETAIL ---- */
    var detail, detailHead, detailMeta, detailPoll, detailThumbs, listing, app;

    function renderDetail(s, opts) {
      opts = opts || {};
      var M = METHOD[s.method];
      /* banner */
      var banner = '';
      if (s.status === 'closed') {
        if (failed(s)) banner = bannerHTML('closed', I.info, esc(t('banner_failed_t', { goal: s.threshold })), t('banner_failed_d', { goal: s.threshold }));
        else { var tr = topResult(s); banner = bannerHTML('closed', I.trophy, esc(t('banner_closed_t') || 'Showdown closed'), t('banner_closed_d', { winner: esc(tr.c.name), pct: tr.pct })); }
      }
      else if (s.status === 'hit') banner = bannerHTML('hit', I.star, esc(t('banner_hit_t', { goal: s.threshold })), t('banner_hit_d'));
      else if (s.voted) banner = bannerHTML('voted', I.check, esc(t('banner_voted_t') || 'Your vote is in'), t('banner_voted_d'));

      /* header media + thumbs */
      detailHead.innerHTML = '<div class="detail__hero-badges"><span class="mtag">' + M.glyph + ' ' + esc(M.tag) + '</span>' + statusBadge(s) + '</div>' + pic(s.hero || (s.candidates[0] && s.candidates[0].img), s.title);
      if (detailThumbs) {
        var imgs = (s.gallery && s.gallery.length ? s.gallery : s.candidates.map(function (c) { return c.img; })).filter(Boolean).slice(0, 4);
        var cells = '';
        for (var i = 0; i < 4; i++) cells += '<div class="thumb">' + pic(imgs[i] || null, t('imgslot_art')) + '</div>';
        detailThumbs.innerHTML = cells;
      }

      /* facts: how it works, plus the goal card when there's a vote-point goal */
      var howFact = fact(M.glyph, t('fact_how') || 'How it works', M.tag, M.rule);
      var facts = s.threshold
        ? '<div class="facts facts--pair">' + howFact + fact(I.star, t('fact_goal') || 'Vote-point goal', s.threshold + ' ' + (t('fact_goal_unit') || 'pts'), t('fact_goal_sub') || 'a signal, not a promise') + '</div>'
        : '<div class="facts facts--single">' + howFact + '</div>';

      var ptsBlock = s.threshold ? '<div>' + meter(s, { lg: true }) + '</div>' : '';
      var tallyBlock = (s.status === 'closed') ? leadLine(s, true) : '';
      var stack;
      if (T.framing === 'tally' && tallyBlock) stack = '<div style="display:flex;flex-direction:column;gap:14px">' + tallyBlock + (s.threshold ? '<div style="opacity:.92">' + meter(s) + '</div>' : '') + '</div>';
      else if (T.framing === 'points') stack = ptsBlock;
      else stack = '<div style="display:flex;flex-direction:column;gap:14px">' + (tallyBlock || '') + ptsBlock + '</div>';

      detailMeta.innerHTML = banner +
        '<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-bottom:4px"><span class="kk-tag kk-tag--soft">' + esc(t('candidates_n', { n: s.candidates.length })) + '</span></div>' +
        '<h1 class="detail__title">' + esc(s.title) + '</h1>' +
        (s.description ? '<p class="detail__sub">' + esc(s.description) + '</p>' : '') +
        (stack ? '<div class="progress-block">' + stack + '</div>' : '') +
        countdownHTML(s) + facts;

      renderPoll(s, opts);
      tickCountdowns();
    }

    function renderPoll(s, opts) {
      opts = opts || {};
      var open = s.status !== 'closed';
      var canEdit = open && loggedIn;
      var editing = canEdit && (!s.voted || s._editing);
      detailPoll.innerHTML = '';
      if (cooldownTimer) { clearTimeout(cooldownTimer); cooldownTimer = null; }

      var head = document.createElement('div');
      head.className = 'vp__sectlabel vp__sectlabel--center';
      head.innerHTML = '<span class="rule"></span><h2>' + esc(editing ? (t('poll_cast') || 'Cast your vote') : (t('poll_candidates') || 'Candidates')) + '</h2><span class="rule"></span>';
      detailPoll.appendChild(head);

      if (editing) {
        var wrap = document.createElement('div'); wrap.className = 'poll';
        var guide = document.createElement('div'); guide.className = 'poll__guide';
        guide.innerHTML = I.spark + '<span>' + esc(METHOD[s.method].verb) + ' · <b>' + esc(METHOD[s.method].rule) + '</b></span><span class="ct" data-selcount></span>';
        wrap.appendChild(guide);
        var tiles = document.createElement('div'); tiles.className = 'ctiles';
        s.candidates.forEach(function (c) { tiles.appendChild(tile(s, c, true)); });
        wrap.appendChild(tiles);
        var ar = document.createElement('div'); ar.className = 'actionrow';
        ar.innerHTML = '<button type="button" class="button" data-submit><span>' + esc(submitLabel(s)) + '</span></button>' + (s.voted ? '<button type="button" class="linkbtn" data-cancel>' + esc(t('keep_vote') || 'Keep my current vote') + '</button>' : '');
        wrap.appendChild(ar);
        detailPoll.appendChild(wrap);
        updateSelCount(s);
        if (T.standings === 'live' && s.points > 0) { var sp = standingsPanel(s); sp.style.marginTop = '26px'; detailPoll.appendChild(sp); animateStandings(sp, s, false); }
        wireVote(s);
      } else {
        if (open && !loggedIn) {
          var blindG = T.standings === 'blind' && s.status !== 'hit';
          var spG = standingsPanel(s, { blind: blindG }); detailPoll.appendChild(spG); animateStandings(spG, s, false);
          var arg = document.createElement('div'); arg.className = 'actionrow';
          var a = document.createElement('a'); a.className = 'button'; a.style.flex = '1'; a.href = root() + '/account/login'; a.textContent = t('login_cta') || 'Log in to vote';
          arg.appendChild(a); detailPoll.appendChild(arg);
        } else {
          var blind = T.standings === 'blind' && open && s.status !== 'hit';
          var sp2 = standingsPanel(s, { blind: blind }); detailPoll.appendChild(sp2); animateStandings(sp2, s, opts.celebrate);
          if (open) {
            var left = cooldownLeft(s.slug);
            var ar2 = document.createElement('div'); ar2.className = 'actionrow';
            if (left > 0) {
              // Locked: still inside the 5-min window (and/or the POST is in flight).
              ar2.innerHTML = '<button type="button" class="button button--secondary" data-change disabled aria-disabled="true">' + esc(t('change_vote') || 'Change my vote') + '</button>';
              detailPoll.appendChild(ar2);
              var note = document.createElement('p'); note.className = 'kksd-cooldown'; note.textContent = t('cooldown_note') || 'You can only change your vote once every 5 minutes';
              detailPoll.appendChild(note);
              // Re-render once the lock lifts so the button re-enables on its own.
              cooldownTimer = setTimeout(function () { if (current === s.id || detailOnly) { var f = find(s.id) || s; renderPoll(f); } }, left + 250);
            } else {
              ar2.innerHTML = '<button type="button" class="button button--secondary" data-change>' + esc(t('change_vote') || 'Change my vote') + '</button>';
              detailPoll.appendChild(ar2);
              ar2.querySelector('[data-change]').addEventListener('click', function () { s._editing = true; renderPoll(s); });
            }
          }
        }
      }
    }
    function submitLabel(s) {
      var ch = s.voted;
      if (s.method === 'favourite') return ch ? (t('submit_fav_upd') || 'Update my pick') : (t('submit_fav') || 'Cast my vote');
      if (s.method === 'approve') return ch ? (t('submit_approve_upd') || 'Update approvals') : (t('submit_approve') || 'Submit approvals');
      return ch ? (t('submit_ranked_upd') || 'Update my ranking') : (t('submit_ranked') || 'Submit ranking');
    }
    function updateSelCount(s) {
      var el = detailPoll.querySelector('[data-selcount]'); if (!el) return;
      var n = selCount(s);
      el.textContent = s.method === 'favourite' ? (n ? (t('count_picked') || '1 picked') : (t('count_choose') || 'choose 1')) : (s.method === 'approve' ? t('count_approved', { n: n }) : t('count_ranked', { n: n }));
      var btn = detailPoll.querySelector('[data-submit]');
      if (btn) { if (n > 0) btn.removeAttribute('aria-disabled'); else btn.setAttribute('aria-disabled', 'true'); }
    }

    /* ---- vote interactions ---- */
    function wireVote(s) {
      var sub = detailPoll.querySelector('[data-submit]');
      if (sub) sub.addEventListener('click', function () { if (sub.getAttribute('aria-disabled') === 'true') { shake(sub); return; } submitVote(s, sub); });
      var cancel = detailPoll.querySelector('[data-cancel]');
      if (cancel) cancel.addEventListener('click', function () { s._editing = false; restoreSel(s); renderPoll(s); });
    }
    function pickCandidate(s, cid) {
      var m = s.mySel;
      if (s.method === 'favourite') m.pick = cid;
      else if (s.method === 'approve') { m.approve = m.approve || []; var i = m.approve.indexOf(cid); if (i > -1) m.approve.splice(i, 1); else m.approve.push(cid); }
      else { m.rank = m.rank || []; var j = m.rank.indexOf(cid); if (j > -1) m.rank.splice(j, 1); else m.rank.push(cid); }
      renderPoll(s);
      var fresh = detailPoll.querySelector('.ctile[data-cid="' + cid + '"]');
      if (fresh && isMine(s, cid)) { fresh.classList.add('just-picked'); setTimeout(function () { fresh.classList.remove('just-picked'); }, 440); }
    }
    function selectedIds(s) {
      if (s.method === 'favourite') return s.mySel.pick ? [s.mySel.pick] : [];
      if (s.method === 'approve') return (s.mySel.approve || []).slice();
      return (s.mySel.rank || []).slice();
    }
    function restoreSel(s) { /* revert in-progress edits to the server truth */
      var my = s._serverSel || {};
      s.mySel = s.method === 'favourite' ? { pick: my.pick || null } : s.method === 'approve' ? { approve: (my.approve || []).slice() } : { rank: (my.rank || []).slice() };
    }
    /* Optimistic submit: record the vote locally, flip to the success state +
       celebrate immediately, then POST in the background. When the POST resolves
       we RECONCILE against the server (a silent reload) so the standings reflect
       the real tally — never a client-side guess, which would drift on re-votes,
       concurrent voters, or Borda. On a transient failure we retry once quickly;
       on a terminal failure the reload resyncs to truth and reopens the form with
       the attempted picks + an error toast. keepalive lets the POST survive page
       unload; an AbortController cancels a superseded re-vote. */
    function cloneSel(sel) { sel = sel || {}; return { pick: sel.pick || null, approve: (sel.approve || []).slice(), rank: (sel.rank || []).slice() }; }
    function selToMySel(method, ids) {
      if (method === 'favourite') return { pick: ids[0] || null };
      if (method === 'approve') return { approve: ids.slice() };
      return { rank: ids.slice() };
    }
    // Apply the new vote to local counts as a delta from the previous (displayed)
    // selection, so re-voting moves a vote instead of adding one. This is only the
    // instant estimate — postVote reconciles it against the server tally.
    function applyOptimisticVote(s, ids, firstTime) {
      var old = s._serverSel || {}, m = s.method, newSet = {};
      ids.forEach(function (id) { newSet[id] = true; });
      var oldRank = old.rank || [], newRank = m === 'ranked' ? ids : [], oldK = oldRank.length, newK = newRank.length;
      function wasSel(id) {
        if (m === 'favourite') return old.pick === id;
        if (m === 'approve') return (old.approve || []).indexOf(id) > -1;
        return oldRank.indexOf(id) > -1;
      }
      s.candidates.forEach(function (c) {
        var now = !!newSet[c.id], was = wasSel(c.id);
        c.votes = Math.max(0, (c.votes || 0) + (now ? 1 : 0) - (was ? 1 : 0));
        if (m === 'ranked') {
          var oi = oldRank.indexOf(c.id), ni = newRank.indexOf(c.id);
          c.weight = Math.max(0, (c.weight || 0) + (ni > -1 ? newK - ni : 0) - (oi > -1 ? oldK - oi : 0));
        } else { c.weight = c.votes; }
      });
      s.voted = true;
      if (firstTime) s.points = (s.points || 0) + 1;
      s._st = null;
    }
    // True when the server's recorded selection matches the attempted ids — lets
    // a "failure" whose vote actually landed (a lost response) resolve as success.
    function sameSel(method, mySel, ids) {
      mySel = mySel || {};
      if (method === 'favourite') return (mySel.pick || null) === (ids[0] || null);
      var a = method === 'ranked' ? (mySel.rank || []) : (mySel.approve || []);
      if (a.length !== ids.length) return false;
      if (method === 'ranked') { for (var i = 0; i < a.length; i++) if (a[i] !== ids[i]) return false; return true; }
      var set = {}; a.forEach(function (x) { set[x] = 1; }); return ids.every(function (x) { return set[x]; });
    }
    // Reload server truth, then re-render the detail in place (and the hub cards).
    // _pct is carried so the standings animate from the current values, not zero.
    function reconcileVote(id, opts) {
      opts = opts || {};
      load(function () {
        var fresh = find(id);
        var err = function () { toast('', I.info, t('toast_err_t') || 'Couldn’t save your vote', t('toast_err_d') || 'Please try again in a moment.'); };
        if (!fresh) { if (opts.toast) err(); return; }
        if (opts.pct) fresh._pct = opts.pct;
        // The reload is the source of truth: if the server actually recorded the
        // attempted vote (e.g. a lost response), treat it as success — don't reopen
        // or toast. Only a vote that genuinely didn't land reopens the form.
        var landed = opts.reopen && opts.ids && sameSel(fresh.method, fresh.mySel, opts.ids);
        var reopen = opts.reopen && !landed;
        var serverSel = cloneSel(fresh.mySel); // the real selection the server holds
        if (reopen) { fresh._editing = true; if (opts.ids) fresh.mySel = selToMySel(fresh.method, opts.ids); }
        if (detailOnly || current === id) openDetailRender(fresh);
        // openDetailRender set _serverSel from mySel; on a reopen that's the
        // attempted (rejected) picks — pin it back to what the server actually has.
        if (reopen) fresh._serverSel = serverSel;
        if (opts.toast && !landed) err();
      });
    }
    function postVote(s, ids, firstTime, attempt, seq, signal) {
      // One quick retry for transient errors only, so the failure path resolves
      // fast rather than reverting long after the click.
      var retry = function () {
        if (attempt >= 1) return false;
        setTimeout(function () { if (s._voteSeq === seq) postVote(s, ids, firstTime, attempt + 1, seq, signal); }, 600);
        return true;
      };
      var fail = function () { reconcileVote(s.id, { pct: s._pct, reopen: true, ids: ids, toast: true }); };
      fetch(root() + '/apps/rewards/vote', {
        // keepalive: the POST survives page unload, so navigating away right after
        // voting doesn't silently drop it.
        method: 'POST', credentials: 'same-origin', keepalive: true, signal: signal,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ showdown: s.slug, candidates: ids }),
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, status: r.status, j: j }; }, function () { return { ok: r.ok, status: r.status, j: null }; }); })
        .then(function (res) {
          if (s._voteSeq !== seq) return; // superseded by a newer vote
          if (res.ok && res.j && res.j.ok) {
            if (res.j.points_awarded && window.KKRewards) window.KKRewards.invalidate();
            reconcileVote(s.id, { pct: s._pct }); // silent resync to the real tally
            return;
          }
          // Definite rejection (4xx) won't change on retry; transient errors get one quick retry.
          var transient = !res.status || res.status >= 500 || res.status === 429 || res.status === 408;
          if (transient && retry()) return;
          fail();
        })
        .catch(function (err) {
          if ((err && err.name === 'AbortError') || s._voteSeq !== seq) return; // cancelled / superseded
          if (!retry()) fail();
        });
    }
    function submitVote(s, btn) {
      var ids = selectedIds(s);
      if (!ids.length) { shake(btn); return; }
      var firstTime = !s.voted, beforeVoters = s.points, goal = s.threshold;
      // Cancel any in-flight submit for this showdown (rapid re-vote) so the stale
      // request never reaches the server, then start a fresh attempt.
      if (s._voteAbort) { try { s._voteAbort.abort(); } catch (e) { /* ignore */ } }
      var ac = (typeof AbortController !== 'undefined') ? new AbortController() : null;
      s._voteAbort = ac;
      var seq = (s._voteSeq = (s._voteSeq || 0) + 1);
      applyOptimisticVote(s, ids, firstTime);
      markVoted(s.slug); // start the 5-min change-vote cooldown (covers the in-flight POST too)
      s._editing = false;
      var crossed = goal && beforeVoters < goal && s.points >= goal;
      openDetailRender(s, { celebrate: true });   // remembers _serverSel = the new (optimistic) pick
      celebrate(s, firstTime, crossed);
      postVote(s, ids, firstTime, 0, seq, ac ? ac.signal : undefined);
    }

    /* ---- celebration ---- */
    function celebrate(s, firstTime, crossed) {
      var anchor = detailMeta.querySelector('.meter') || detailPoll;
      var rect = anchor.getBoundingClientRect();
      starBurst(rect.left + rect.width / 2, rect.top + rect.height / 2);
      if (crossed) { toast('big', I.star, t('toast_goal_t', { goal: s.threshold }), t('toast_goal_d')); setTimeout(function () { starBurst(window.innerWidth * 0.3, window.innerHeight * 0.4); }, 160); setTimeout(function () { starBurst(window.innerWidth * 0.7, window.innerHeight * 0.4); }, 300); }
      else { var L = leader(s); toast('', I.check, firstTime ? (t('toast_counted_t') || 'Vote counted') : (t('toast_updated_t') || 'Vote updated'), t(firstTime ? 'toast_counted_d' : 'toast_updated_d', { name: esc(L.c.name), pct: L.pct })); }
    }
    var toastWrap;
    function toast(kind, ic, head, desc) {
      if (!toastWrap) { toastWrap = document.createElement('div'); toastWrap.className = 'kksd-toast-wrap'; document.body.appendChild(toastWrap); }
      var el = document.createElement('div'); el.className = 'kksd-toast' + (kind === 'big' ? ' kksd-toast--big' : '');
      el.innerHTML = '<span class="kksd-toast__ic">' + ic + '</span><div><div class="kksd-toast__t">' + head + '</div><div class="kksd-toast__d">' + desc + '</div></div>';
      toastWrap.appendChild(el);
      setTimeout(function () { el.classList.add('out'); setTimeout(function () { el.remove(); }, 360); }, kind === 'big' ? 4200 : 3000);
    }
    function starBurst(cx, cy) {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      var host = document.createElement('div'); host.className = 'kksd-burst'; host.style.left = cx + 'px'; host.style.top = cy + 'px'; document.body.appendChild(host);
      var colors = ['#FF3D9A', '#f279a1', '#E0277F', '#FFC9E0', '#11041a'], N = 18;
      for (var i = 0; i < N; i++) {
        var p = document.createElement('i'), sz = 8 + Math.random() * 12;
        p.style.width = sz + 'px'; p.style.height = sz + 'px'; p.style.color = colors[i % colors.length];
        p.innerHTML = (i % 3 === 0) ? I.star : (i % 3 === 1 ? I.spark : I.heart);
        host.appendChild(p);
        var ang = (Math.PI * 2 * i) / N + Math.random() * 0.5, dist = 60 + Math.random() * 90, dx = Math.cos(ang) * dist, dy = Math.sin(ang) * dist - 20;
        p.animate([{ transform: 'translate(-50%,-50%) translate(0,0) scale(0)', opacity: 1 }, { transform: 'translate(-50%,-50%) translate(' + dx * 0.6 + 'px,' + dy * 0.6 + 'px) scale(1.1)', opacity: 1, offset: 0.55 }, { transform: 'translate(-50%,-50%) translate(' + dx + 'px,' + (dy + 70) + 'px) scale(.5)', opacity: 0 }], { duration: 900 + Math.random() * 400, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'forwards' });
      }
      setTimeout(function () { host.remove(); }, 1500);
    }
    function shake(el) { el.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-6px)' }, { transform: 'translateX(6px)' }, { transform: 'translateX(0)' }], { duration: 260 }); }

    /* ---- nav ---- */
    function rememberServerSel(s) { s._serverSel = s.method === 'favourite' ? { pick: s.mySel.pick } : s.method === 'approve' ? { approve: (s.mySel.approve || []).slice() } : { rank: (s.mySel.rank || []).slice() }; }
    function openDetailRender(s, opts) { rememberServerSel(s); renderDetail(s, opts); }
    function openDetail(id, push) {
      var s = find(id); if (!s) return;
      current = id; s._editing = false;
      openDetailRender(s);
      if (app) { app.classList.add('is-detail'); }
      if (!detailOnly) {
        if (push) { try { history.pushState({ kksd: id }, '', basePath + '?showdown=' + encodeURIComponent(id)); } catch (e) {} }
        window.scrollTo({ top: rootEl.getBoundingClientRect().top + window.pageYOffset - 8, behavior: 'auto' });
      }
    }
    function closeDetail(push) {
      current = null;
      if (app) app.classList.remove('is-detail');
      if (push) { try { history.pushState({}, '', basePath); } catch (e) {} }
    }
    function syncFromUrl() {
      var slug = new URLSearchParams(window.location.search).get('showdown');
      if (slug && find(slug)) openDetail(slug, false); else closeDetail(false);
    }

    /* ---- render ---- */
    function renderList() {
      var live = rootEl.querySelector('[data-grid="live"]'), done = rootEl.querySelector('[data-grid="done"]');
      var doneLabel = rootEl.querySelector('.kksd__sectlabel--done');
      if (!live) return;
      live.replaceChildren(); if (done) done.replaceChildren(); live.removeAttribute('aria-busy');
      var open = [], closed = [];
      DATA.forEach(function (s) { (s.status !== 'closed' ? open : closed).push(s); });
      if (!open.length) live.appendChild(emptyEl(rootEl.dataset.empty || ''));
      else open.forEach(function (s) { live.appendChild(card(s)); });
      var has = closed.length > 0;
      if (doneLabel) doneLabel.hidden = !has;
      if (done) { done.hidden = !has; closed.forEach(function (s) { done.appendChild(card(s)); }); }
    }
    function emptyEl(txt) { var p = document.createElement('p'); p.className = 'kksd__empty'; p.textContent = txt || 'No showdowns yet. Check back soon.'; return p; }
    /* first-paint loading skeletons for the open grid (KKSkel, the sitewide shimmer) */
    function skeletons() {
      if (detailOnly) return;
      var live = rootEl.querySelector('[data-grid="live"]');
      if (!live || !window.KKSkel) return;
      live.replaceChildren();
      for (var i = 0; i < 3; i++) {
        var c = document.createElement('div');
        c.className = 'vcard vcard--skel'; c.setAttribute('aria-hidden', 'true');
        var media = document.createElement('div'); media.className = 'vcard__media';
        media.appendChild(window.KKSkel.el({ fill: true }));
        var body = document.createElement('div'); body.className = 'vcard__body';
        body.appendChild(window.KKSkel.el({ w: '72%', h: 18 }));
        body.appendChild(window.KKSkel.el({ w: '45%', h: 12 }));
        body.appendChild(window.KKSkel.el({ w: '100%', h: 10, radius: 999, style: 'margin-top:6px;' }));
        c.appendChild(media); c.appendChild(body);
        live.appendChild(c);
      }
    }
    /* first-paint skeleton for a deep-linked / candidate detail (so a reload on a
       detail URL shows the detail loading, not the full listing grid). */
    function detailSkeleton() {
      if (!window.KKSkel) return;
      if (detailHead) detailHead.innerHTML = window.KKSkel.html({ fill: true });
      if (detailThumbs) {
        var cells = '';
        for (var i = 0; i < 4; i++) cells += '<div class="thumb">' + window.KKSkel.html({ fill: true }) + '</div>';
        detailThumbs.innerHTML = cells;
      }
      if (detailMeta) detailMeta.innerHTML =
        window.KKSkel.html({ w: '38%', h: 16, style: 'margin-bottom:16px;' }) +
        window.KKSkel.html({ w: '85%', h: 38, style: 'margin-bottom:18px;' }) +
        window.KKSkel.html({ w: '100%', h: 12, style: 'margin-bottom:8px;' }) +
        window.KKSkel.html({ w: '92%', h: 12, style: 'margin-bottom:8px;' }) +
        window.KKSkel.html({ w: '60%', h: 12 });
    }

    function render() {
      if (detailOnly) {
        var s = find(slugFilter);
        if (s) { openDetailRender(s); if (app) app.classList.add('is-detail'); }
        else if (detailMeta) detailMeta.innerHTML = '<p class="kksd__empty">' + esc(rootEl.dataset.empty || 'This vote isn’t open right now.') + '</p>';
        return;
      }
      renderList();
      if (current && find(current)) openDetail(current, false);
      else syncFromUrl();
    }

    function load(after) {
      fetch(root() + '/apps/rewards/showdowns', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          DATA = normalise((j && j.showdowns) || []);
          loggedIn = !!(j && j.logged_in);
          reloadQueued = false;
          // After a vote the caller re-renders just the detail; refresh the
          // background list (if present) but skip render()'s detail re-open so
          // the detail isn't painted twice.
          if (after) { if (!detailOnly) renderList(); after(); }
          else render();
        })
        .catch(function () {
          var live = rootEl.querySelector('[data-grid="live"]');
          if (live) { live.removeAttribute('aria-busy'); live.replaceChildren(errEl(t('load_err') || 'Couldn’t load showdowns right now.')); }
          if (detailOnly && detailMeta) detailMeta.innerHTML = '<p class="kksd__empty kksd__empty--err">' + esc(t('load_err') || 'Couldn’t load this showdown right now.') + '</p>';
        });
    }
    function errEl(txt) { var p = document.createElement('p'); p.className = 'kksd__empty kksd__empty--err'; p.textContent = txt; return p; }

    /* ---- boot this instance ---- */
    app = rootEl.querySelector('[data-app]');
    listing = rootEl.querySelector('.listing');
    detail = rootEl.querySelector('.detail');
    detailHead = rootEl.querySelector('[data-detail-head]');
    detailMeta = rootEl.querySelector('[data-detail-meta]');
    detailPoll = rootEl.querySelector('[data-detail-poll]');
    detailThumbs = rootEl.querySelector('[data-detail-thumbs]');
    var backBtn = rootEl.querySelector('[data-back]');
    if (backBtn) { if (detailOnly) backBtn.hidden = true; else backBtn.addEventListener('click', function () { closeDetail(true); }); }
    if (!detailOnly) window.addEventListener('popstate', function () { if (DATA.length) syncFromUrl(); });
    setInterval(tickCountdowns, 1000);
    // Deep-linked to a detail (?showdown=…) or a candidate page → open straight
    // to a detail skeleton, not the listing grid skeleton.
    var deepSlug = detailOnly ? slugFilter : new URLSearchParams(window.location.search).get('showdown');
    if (deepSlug) { if (app) app.classList.add('is-detail'); detailSkeleton(); }
    else skeletons();
    load();
  }

  function init() { document.querySelectorAll('.kkumi-showdowns').forEach(initInstance); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
